'use client';

import clsx from 'clsx';
import { type Ref, useSyncExternalStore } from 'react';

import { DataTestIds } from '@repo/frontend-shared';

import '@repo/sds/components/_internal/SDSSkeleton/SDSSkeleton.scss';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

function getReducedMotionQuery(): MediaQueryList | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.matchMedia?.(REDUCED_MOTION_QUERY);
  } catch {
    return undefined;
  }
}

function subscribeToReducedMotion(onChange: () => void): () => void {
  const query = getReducedMotionQuery();
  query?.addEventListener?.('change', onChange);
  return () => query?.removeEventListener?.('change', onChange);
}

/** Helper to safely detect reduced motion preference */
function getReducedMotionPreference(): boolean {
  return getReducedMotionQuery()?.matches === true;
}

// The server cannot know the preference, so hydration starts from the animated markup it sent.
function getServerReducedMotionPreference(): boolean {
  return false;
}

export interface SDSSkeletonProps {
  /** Width of the skeleton element */
  width?: string | number;
  /** Height of the skeleton element */
  height?: string | number;
  /** Border radius variant */
  radius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  /** Additional CSS classes */
  className?: string;
  /** Inline styles */
  style?: React.CSSProperties;
  /** Animation variant */
  animation?: 'wave' | 'pulse' | 'none';
  /** Custom animation duration in seconds */
  duration?: number;
  ref?: Ref<HTMLDivElement>;
}

/**
 * Skeleton component for loading states.
 * Skeletons are decorative and hidden from screen readers.
 *
 * For accessibility, announce loading states at the page/section level, not on individual skeletons.
 */
export function SDSSkeleton({
  width = '100%',
  height = '100%',
  radius = 'medium',
  className,
  style,
  animation = 'wave',
  duration = 1.5,
  ref,
}: SDSSkeletonProps) {
  // Robust motion preference detection with error handling
  const prefersReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionPreference,
    getServerReducedMotionPreference,
  );

  // Determine final animation
  const finalAnimation = prefersReducedMotion ? 'none' : animation;

  // CSS custom properties for theming
  const cssProps = {
    '--skeleton-duration': `${duration}s`,
    width,
    height,
    ...style,
  } as React.CSSProperties;

  return (
    <div
      ref={ref}
      className={clsx(
        'SDSSkeleton',
        `SDSSkeleton--radius-${radius}`,
        `SDSSkeleton--animation-${finalAnimation}`,
        {
          'SDSSkeleton--reduced-motion': prefersReducedMotion,
        },
        className,
      )}
      style={cssProps}
      aria-hidden="true"
      data-testid={DataTestIds.SDS.SDS_SKELETON}
    />
  );
}
